import React from "react";
import * as api from "../api";
import {
  AlertTriangle, ArrowUpRight, Check, CheckCircle2, ChevronDown, Clock, Copy,
  CreditCard, Crown, DollarSign, Download, Edit3, Eye, Flame, Gamepad2,
  KeyRound, Layers, Lock, MessageCircle, Package, Paperclip, Plus, RefreshCw,
  ScanLine, Search, Send, ShieldAlert, ShieldCheck, ShoppingCart, Sparkles,
  Trash2, Trophy, Upload, User, Wallet, X, XCircle
} from "lucide-react";
import { Modal, MODAL_HEADER, MODAL_ACTIONS } from "../components/shared/Modal";
import { FormField } from "../components/shared/FormField";
import { OCRProgress } from "../components/shared/OCRProgress";
import { CredRow } from "../components/shared/CredRow";
import { AccessMethodsEditor } from "../components/admin/AccessMethodsEditor";
import { StatusBadge, UserStatusBadge } from "../components/shared/StatusBadges";
import { WalletChip } from "../components/shared/WalletChip";
import { SocialProofFeed } from "../components/shared/SocialProofFeed";
import { ChatDrawer } from "../components/chat/ChatDrawer";
import { ForensicReport } from "../components/game/ForensicReport";
import { RechargeSuccessCelebration } from "../components/game/RechargeSuccessCelebration";
import { celebrationGameKey } from "../components/game/rechargeCelebrationConfig";
import { WelcomeModal } from "../components/WelcomeModal";
import { PaymentProofModal } from "../components/modals/PaymentProofModal";
import { ConfirmOrderModal } from "../components/modals/ConfirmOrderModal";
import { UserProfileModal } from "../components/modals/UserProfileModal";
import { AdjustmentRequestModal } from "../components/modals/AdjustmentRequestModal";
import { AdjustmentPayModal } from "../components/modals/AdjustmentPayModal";
import { ManualVerifyModal } from "../components/modals/ManualVerifyModal";
import { useAppContext } from "../AppContext";

/* -----------------------------------------------------------------------------
 * AppModals — all app-wide modals (payment, welcome, game/package/bank editors,
 * proof preview, rejection, support, client history, chat drawer, completed
 * order confetti…) extracted from the previously monolithic App.js.
 *
 * State/handlers arrive through the shared AppContext bag so the JSX matches
 * the original 1:1.
 * --------------------------------------------------------------------------- */
export function AppModals() {
  const ctx = useAppContext();
  const {
    // Core
    user, showToast, activeRole, currency, exchangeRate, siteStatus,
    setUser, setClientTab, socialNetworks, tour,
    // Formatters
    formatBs, formatUsd, formatItemMoney, copyToClipboard,
    // Cart / totals
    cart, cartTotalBs, cartTotalUsd, remainingAfterCreditBs,
    // Payment modal
    showPaymentModal, setShowPaymentModal, submitting,
    paymentMethods, selectedPmId, setSelectedPmId,
    checkoutPmTab, setCheckoutPmTab, paidAmountBs, setPaidAmountBs,
    referenceNumber, setReferenceNumber, needsManualRef, setNeedsManualRef,
    proofFile, setProofFile, proofFileName, setProofFileName,
    useAccountCredit, setUseAccountCredit,
    validationErrors, handleSubmitPayment,
    // Payment result modal
    paymentResultModal, setPaymentResultModal, lastOrderResult,
    // Complete partial modal
    completePartialOrder, setCompletePartialOrder, partialAmount, setPartialAmount,
    partialRef, setPartialRef, partialFile, setPartialFile,
    partialFileName, setPartialFileName, completingPartial, partialErrors,
    handleSubmitPartialPayment, remainingForOrder,
    // Preview
    previewProofUrl, setPreviewProofUrl,
    // Game modal
    showGameModal, setShowGameModal, editingGame, gameForm, setGameForm,
    savingGame, handleSaveGame, handleGameImageFile,
    deleteGameConfirm, setDeleteGameConfirm, handleConfirmDeleteGame,
    // Package modal
    showAddPkgModal, setShowAddPkgModal, editingPkg,
    pkgGameId, setPkgGameId, pkgNameInput, setPkgNameInput,
    pkgPriceUsdInput, setPkgPriceUsdInput, pkgDescriptionInput, setPkgDescriptionInput,
    pkgImageInput, setPkgImageInput, pkgResellerPriceUsdInput, setPkgResellerPriceUsdInput,
    pkgCostPriceUsdInput, setPkgCostPriceUsdInput,
    pkgRequiresVerify, setPkgRequiresVerify, pkgIsBestSellerInput, setPkgIsBestSellerInput,
    pkgVerifyTitle, setPkgVerifyTitle, pkgVerifyDescription, setPkgVerifyDescription,
    pkgVerifyImageUrl, setPkgVerifyImageUrl, pkgVerifyStepsText, setPkgVerifyStepsText,
    handleSavePackage, handlePkgImageFile, handlePkgVerifyImageFile,
    games,
    // Bank / payment method modal
    showAddBankModal, setShowAddBankModal, bankForm, setBankForm,
    editingPm, setEditingPm, handleAddBank,
    // Chat drawer
    chatOrder, setChatOrder,
    // Welcome modal
    showWelcomeModal, setShowWelcomeModal,
    // Client history modal (admin)
    clientHistory, setClientHistory, clientHistoryLoading,
    // Reject order modal
    rejectOrderModal, setRejectOrderModal, rejectReason, setRejectReason,
    rejectingOrder, handleConfirmRejectOrder,
    reclaimSurplusOnReject, setReclaimSurplusOnReject,
    // Delete user modal
    deleteUserConfirm, setDeleteUserConfirm, handleDeleteUser,
    // Status change modal
    statusChangeModal, setStatusChangeModal,
    statusChangeReason, setStatusChangeReason, handleChangeUserStatus,
    // Support modal
    showSupportModal, setShowSupportModal, supportDesc, setSupportDesc,
    supportFile, setSupportFile, supportOrderId, setSupportOrderId,
    supportSubmitting, handleSubmitSupport, orders,
    // Completed order banner
    completedNotif, acknowledgeCompletionOrders,
    loadOrders, openSupportModal, playChimeSound,
    // New order / chat banners
    newOrderBanner, setNewOrderBanner, newChatBanner, setNewChatBanner,
  } = ctx;

  return (
    <>
      {/* PAYMENT + RESULT — extracted to /components/modals/PaymentProofModal.jsx */}
      <PaymentProofModal />
      {/* COMPLETE PARTIAL PAYMENT — extracted to /components/modals/ConfirmOrderModal.jsx */}
      <ConfirmOrderModal />

      {/* ============ PROOF PREVIEW MODAL ============ */}
      {previewProofUrl && (() => {
        const resolvedProofUrl = api.resolveFileUrl(previewProofUrl);
        return (
        <Modal onClose={() => setPreviewProofUrl(null)} size="md" testId="proof-preview-modal">
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-[var(--ag7-border)] pb-3">
              <h3 className="text-sm font-bold text-white">Comprobante de Pago</h3>
              <a data-testid="app-modals-a-1" href={resolvedProofUrl} download={`comprobante-${Date.now()}.png`}
                className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs rounded-lg flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Descargar
              </a>
            </div>
            <img src={resolvedProofUrl} alt="Comprobante" className="w-full rounded-xl max-h-[70vh] object-contain bg-ag7-bg" />
          </div>
        </Modal>
        );
      })()}

      {/* ============ ADD/EDIT PACKAGE MODAL ============ */}
      {showAddPkgModal && (
        <Modal onClose={() => setShowAddPkgModal(false)} size="sm" testId="add-package-modal">
          <div className={MODAL_HEADER}>
            <h3 className="text-lg font-heading font-extrabold tracking-tight text-white">{editingPkg ? "Editar Paquete" : "Agregar Paquete"}</h3>
            <button data-testid="app-modals-button-2" onClick={() => setShowAddPkgModal(false)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full"><X className="w-5 h-5" /></button>
          </div>
          <form data-testid="app-modals-form-3" onSubmit={handleSavePackage} className="space-y-4">
            {!editingPkg && (
              <div>
                <label className="block text-xs font-mono text-zinc-400 mb-1">Juego</label>
                <select value={pkgGameId} onChange={(e) => setPkgGameId(e.target.value)} data-testid="pkg-game-select"
                  className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-3 py-2.5 text-white text-xs font-mono">
                  {games?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            )}
            <FormField data-testid="pkg-name-input" label="Nombre del Paquete" testId="pkg-name-input" required value={pkgNameInput} onChange={setPkgNameInput} placeholder="Ej. 1000 Diamantes" />
            <FormField data-testid="pkg-price-input" label="Precio en USD ($)" testId="pkg-price-input" type="number" step="0.01" required value={pkgPriceUsdInput} onChange={setPkgPriceUsdInput} placeholder="9.99" />
            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-amber-400" /> Precio Revendedor USD (opcional)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                data-testid="pkg-reseller-price-input"
                value={pkgResellerPriceUsdInput}
                onChange={(e) => setPkgResellerPriceUsdInput(e.target.value)}
                placeholder="8.50 (déjalo vacío para usar el precio normal)"
                className="w-full bg-ag7-bg border border-amber-500/30 rounded-xl px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
              />
              <p className="text-[10px] font-mono text-zinc-500 mt-1">
                Solo usuarios con rol &quot;Revendedor&quot; pagarán este precio. Vacío o 0 = usa el precio estándar.
              </p>
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-fuchsia-400" /> Costo / Inversión USD (privado)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                data-testid="pkg-cost-price-input"
                value={pkgCostPriceUsdInput}
                onChange={(e) => setPkgCostPriceUsdInput(e.target.value)}
                placeholder="5.00 (lo que te cuesta el paquete al proveedor)"
                className="w-full bg-ag7-bg border border-fuchsia-500/30 rounded-xl px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-fuchsia-500"
              />
              <p className="text-[10px] font-mono text-zinc-500 mt-1">
                NUNCA se muestra al cliente. Se envía a Supabase.sales para calcular la ganancia neta en el dashboard.
              </p>
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">
                Descripción (opcional, visible al cliente)
              </label>
              <textarea
                value={pkgDescriptionInput}
                onChange={(e) => setPkgDescriptionInput(e.target.value)}
                data-testid="pkg-description-input"
                rows={3}
                maxLength={280}
                placeholder="Ej. Incluye bono semanal + 100 diamantes extra por evento."
                className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-purple resize-none"
              />
              <p className="text-[10px] font-mono text-zinc-500 mt-1">{pkgDescriptionInput.length}/280</p>
            </div>

            {/* Package logo (optional) */}
            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">
                Logo del Paquete (opcional)
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="pkg-image-preview">
                  {pkgImageInput
                    ? <img src={pkgImageInput} alt="Logo" className="w-full h-full object-cover" />
                    : <Package className="w-6 h-6 text-zinc-600" />}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="url"
                    value={pkgImageInput.startsWith("data:") ? "" : pkgImageInput}
                    onChange={(e) => setPkgImageInput(e.target.value)}
                    data-testid="pkg-image-url-input"
                    placeholder="URL de imagen (o sube un archivo)"
                    className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-purple"
                  />
                  <div className="flex items-center gap-2">
                    <label className="flex-1 cursor-pointer bg-ag7-surface-2 hover:bg-zinc-700 text-white text-[11px] font-mono py-1.5 px-3 rounded-lg text-center border border-[var(--ag7-border-soft)]">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                        className="hidden"
                        onChange={(e) => handlePkgImageFile(e.target.files?.[0])}
                        data-testid="pkg-image-file-input"
                      />
                      <span className="flex items-center gap-1.5 justify-center">
                        <Upload className="w-3.5 h-3.5" /> Subir archivo (≤2 MB)
                      </span>
                    </label>
                    {pkgImageInput && (
                      <button
                        type="button"
                        onClick={() => setPkgImageInput("")}
                        data-testid="pkg-image-clear-btn"
                        className="bg-red-950/60 hover:bg-red-900 text-red-300 p-1.5 rounded-lg border border-red-900/50"
                        title="Quitar logo"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-[10px] font-mono text-zinc-500 mt-1">
                Se mostrará como miniatura en la tarjeta del paquete al cliente.
              </p>
            </div>

            {/* Restricted / one-time purchase verification (e.g. Pase de Nivel) */}
            <div className="rounded-xl border border-amber-500/25 bg-amber-950/10 p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="pkg-best-seller-toggle"
                  checked={pkgIsBestSellerInput}
                  onChange={(e) => setPkgIsBestSellerInput(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-orange-500"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono font-bold text-orange-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5" /> Marcar como &quot;MÁS POPULAR&quot;
                  </p>
                  <p className="text-[11px] text-zinc-400 leading-snug mt-0.5">
                    Muestra un badge naranja destacado en la tarjeta del cliente para impulsar este paquete.
                  </p>
                </div>
              </label>
            </div>

            <div className="rounded-xl border border-amber-500/25 bg-amber-950/10 p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="pkg-requires-verify-toggle"
                  checked={pkgRequiresVerify}
                  onChange={(e) => setPkgRequiresVerify(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-500"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono font-bold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Requiere verificación previa
                  </p>
                  <p className="text-[11px] text-zinc-400 leading-snug mt-0.5">
                    Actívalo para paquetes de compra única (ej. Pase de Nivel). El cliente verá una advertencia antes de agregarlo al carrito.
                  </p>
                </div>
              </label>

              {pkgRequiresVerify && (
                <div className="space-y-3 pt-2 border-t border-amber-500/20" data-testid="pkg-verify-fields">
                  <div>
                    <label className="block text-[11px] font-mono text-zinc-400 mb-1 uppercase tracking-wider">Título de la advertencia</label>
                    <input
                      type="text"
                      value={pkgVerifyTitle}
                      onChange={(e) => setPkgVerifyTitle(e.target.value)}
                      data-testid="pkg-verify-title-input"
                      maxLength={80}
                      placeholder="Ej. Pase de Nivel — compra única"
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono text-zinc-400 mb-1 uppercase tracking-wider">Descripción</label>
                    <textarea
                      value={pkgVerifyDescription}
                      onChange={(e) => setPkgVerifyDescription(e.target.value)}
                      data-testid="pkg-verify-description-input"
                      rows={3}
                      maxLength={400}
                      placeholder="Explica que es de compra única por cuenta y que si ya fue comprado no recibirán la recarga."
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 resize-none"
                    />
                    <p className="text-[10px] font-mono text-zinc-500 mt-1">{pkgVerifyDescription.length}/400</p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono text-zinc-400 mb-1 uppercase tracking-wider">Imagen ilustrativa (opcional)</label>
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="pkg-verify-image-preview-thumb">
                        {pkgVerifyImageUrl
                          ? <img src={pkgVerifyImageUrl} alt="Guía" className="w-full h-full object-cover" />
                          : <AlertTriangle className="w-5 h-5 text-zinc-600" />}
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <input
                          type="url"
                          value={pkgVerifyImageUrl.startsWith("data:") ? "" : pkgVerifyImageUrl}
                          onChange={(e) => setPkgVerifyImageUrl(e.target.value)}
                          data-testid="pkg-verify-image-url-input"
                          placeholder="URL de imagen (o sube un archivo)"
                          className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                        />
                        <div className="flex items-center gap-2">
                          <label className="flex-1 cursor-pointer bg-ag7-surface-2 hover:bg-zinc-700 text-white text-[11px] font-mono py-1.5 px-3 rounded-lg text-center border border-[var(--ag7-border-soft)]">
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                              className="hidden"
                              onChange={(e) => handlePkgVerifyImageFile(e.target.files?.[0])}
                              data-testid="pkg-verify-image-file-input"
                            />
                            <span className="flex items-center gap-1.5 justify-center">
                              <Upload className="w-3.5 h-3.5" /> Subir archivo (≤2 MB)
                            </span>
                          </label>
                          {pkgVerifyImageUrl && (
                            <button
                              type="button"
                              onClick={() => setPkgVerifyImageUrl("")}
                              data-testid="pkg-verify-image-clear-btn"
                              className="bg-red-950/60 hover:bg-red-900 text-red-300 p-1.5 rounded-lg border border-red-900/50"
                              title="Quitar imagen"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {pkgVerifyImageUrl && (
                      <div className="mt-2 rounded-lg overflow-hidden border border-[var(--ag7-border-soft)] bg-black" data-testid="pkg-verify-image-preview">
                        <img src={pkgVerifyImageUrl} alt="Guía" className="w-full max-h-40 object-contain" />
                      </div>
                    )}
                    <p className="text-[10px] font-mono text-zinc-500 mt-1">
                      Muestra al cliente dónde revisar dentro del juego.
                    </p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono text-zinc-400 mb-1 uppercase tracking-wider">Pasos de verificación (uno por línea)</label>
                    <textarea
                      value={pkgVerifyStepsText}
                      onChange={(e) => setPkgVerifyStepsText(e.target.value)}
                      data-testid="pkg-verify-steps-input"
                      rows={4}
                      placeholder={"Abre Free Fire y entra a tu perfil.\nToca el ícono de Pase de Nivel.\nSi ves 'Ya comprado', no compres esta recarga."}
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 resize-none font-mono"
                    />
                    <p className="text-[10px] font-mono text-zinc-500 mt-1">Máx. 8 pasos.</p>
                  </div>
                </div>
              )}
            </div>

            <div className={MODAL_ACTIONS}>
              <button data-testid="app-modals-button-4" type="button" onClick={() => setShowAddPkgModal(false)} className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-2.5 rounded-xl text-xs">Cancelar</button>
              <button type="submit" data-testid="save-pkg-submit-btn" className="flex-1 bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white font-bold py-2.5 rounded-xl text-xs">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ============ ADD/EDIT GAME MODAL ============ */}
      {showGameModal && (
        <Modal onClose={() => !savingGame && setShowGameModal(false)} size="md" testId="game-modal">
          <div className={MODAL_HEADER}>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-cyan-950/60 border border-cyan-500 flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-lg font-heading font-extrabold tracking-tight text-white">{editingGame ? "Editar Juego" : "Crear Nuevo Juego"}</h3>
            </div>
            <button data-testid="app-modals-button-5" onClick={() => !savingGame && setShowGameModal(false)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full"><X className="w-5 h-5" /></button>
          </div>

          <form data-testid="app-modals-form-6" onSubmit={handleSaveGame} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField data-testid="game-name-input" label="Nombre del Juego" testId="game-name-input" required
                value={gameForm.name} onChange={(v) => setGameForm(f => ({ ...f, name: v }))}
                placeholder="Ej. Mobile Legends" />
              <FormField data-testid="game-publisher-input" label="Publisher (opcional)" testId="game-publisher-input"
                value={gameForm.publisher} onChange={(v) => setGameForm(f => ({ ...f, publisher: v }))}
                placeholder="Ej. Moonton" />
              <FormField data-testid="game-category-input" label="Categoría" testId="game-category-input"
                value={gameForm.category} onChange={(v) => setGameForm(f => ({ ...f, category: v }))}
                placeholder="Ej. MOBA / Battle Royale" />
              <div>
                <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">Tipo de credencial</label>
                <select value={gameForm.credentialType}
                  onChange={(e) => setGameForm(f => ({ ...f, credentialType: e.target.value }))}
                  data-testid="game-credtype-select"
                  className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-3 py-2.5 text-xs font-mono text-white focus:outline-none focus:border-ag7-purple">
                  <option value="id">Solo ID</option>
                  <option value="username_password">Usuario + Contraseña</option>
                  <option value="id_server">ID + Server (Mobile Legends, etc.)</option>
                  <option value="email">Correo (Zinli, Binance, etc.)</option>
                  <option value="phone">Teléfono (Wally, Pago Móvil, etc.)</option>
                </select>
              </div>
              {gameForm.credentialType === "username_password" && (
                <label className="flex items-start gap-2 p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/40 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!gameForm.hasAccessMethod}
                    onChange={(e) => setGameForm(f => ({ ...f, hasAccessMethod: e.target.checked }))}
                    data-testid="game-has-access-method-checkbox"
                    className="mt-0.5 accent-cyan-500 w-4 h-4"
                  />
                  <span className="flex-1">
                    <span className="block text-xs font-bold text-cyan-200">Requiere Forma de Acceso</span>
                    <span className="block text-[10px] text-cyan-300/80 mt-0.5">
                      El comprador elegirá con qué proveedor inicia sesión (ej. Activision, Google, Facebook).
                    </span>
                  </span>
                </label>
              )}
              {gameForm.credentialType === "username_password" && gameForm.hasAccessMethod && (
                <AccessMethodsEditor
                  methods={gameForm.accessMethods || []}
                  onChange={(accessMethods) => setGameForm(f => ({ ...f, accessMethods }))}
                />
              )}
              <FormField data-testid="game-idlabel-input" label="Etiqueta del campo ID" testId="game-idlabel-input"
                value={gameForm.idLabel} onChange={(v) => setGameForm(f => ({ ...f, idLabel: v }))}
                placeholder="Ej. UID de Free Fire" />
              <FormField data-testid="game-idph-input" label="Placeholder del ID" testId="game-idph-input"
                value={gameForm.idPlaceholder} onChange={(v) => setGameForm(f => ({ ...f, idPlaceholder: v }))}
                placeholder="Ej. 8-10 dígitos" />
            </div>

            {/* Image / Logo */}
            <div className="p-4 rounded-2xl bg-ag7-bg border border-[var(--ag7-border-soft)] space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-mono text-zinc-400 uppercase tracking-wider">Imagen / Logo</label>
                {gameForm.image && (
                  <button data-testid="app-modals-button-7" type="button" onClick={() => setGameForm(f => ({ ...f, image: "" }))}
                    className="text-[10px] text-red-300 hover:text-red-200 font-mono">
                    Quitar
                  </button>
                )}
              </div>

              <div className="flex items-start gap-3">
                <div className="w-20 h-20 rounded-xl overflow-hidden border border-[var(--ag7-border-soft)] bg-black flex-shrink-0 flex items-center justify-center">
                  {gameForm.image ? (
                    <img src={gameForm.image} alt="preview" className="w-full h-full object-cover" data-testid="game-image-preview" />
                  ) : (
                    <span className="text-[9px] font-mono text-zinc-500">SIN IMAGEN</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={gameForm.image.startsWith("data:") ? "" : gameForm.image}
                    onChange={(e) => setGameForm(f => ({ ...f, image: e.target.value }))}
                    data-testid="game-image-url-input"
                    placeholder="Pega la URL del logo (https://...)"
                    className="w-full bg-black/60 border border-[var(--ag7-border-soft)] rounded-xl px-3 py-2 text-xs font-mono text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500"
                  />
                  <label className="flex items-center justify-center gap-2 bg-cyan-950/40 hover:bg-cyan-900/40 border border-cyan-500/40 text-cyan-200 text-xs font-bold py-2 rounded-xl cursor-pointer">
                    <Upload className="w-3.5 h-3.5" />
                    <span>Subir archivo (máx. 2MB)</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={(e) => handleGameImageFile(e.target.files?.[0])}
                      data-testid="game-image-file-input"
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              <p className="text-[10px] font-mono text-zinc-500">Puedes pegar una URL pública o subir un archivo desde tu dispositivo.</p>
            </div>

            <div className={MODAL_ACTIONS}>
              <button data-testid="app-modals-button-8" type="button" onClick={() => setShowGameModal(false)} disabled={savingGame}
                className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-2.5 rounded-xl text-xs disabled:opacity-40">
                Cancelar
              </button>
              <button type="submit" disabled={savingGame}
                data-testid="save-game-submit-btn"
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-95 text-white font-bold py-2.5 rounded-xl text-xs disabled:opacity-40 flex items-center justify-center gap-2">
                {savingGame ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>{editingGame ? "Guardar Cambios" : "Crear Juego"}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ============ DELETE GAME CONFIRM ============ */}
      {deleteGameConfirm && (
        <Modal onClose={() => setDeleteGameConfirm(null)} size="sm" testId="delete-game-modal">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-950/60 border-2 border-red-500 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-xl font-extrabold text-white">¿Eliminar Juego?</h3>
            <div className="p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-xs text-zinc-300 space-y-1 font-mono text-left">
              <p><span className="text-zinc-500">Juego:</span> <strong className="text-white">{deleteGameConfirm.name}</strong></p>
              <p><span className="text-zinc-500">Paquetes:</span> <strong className="text-cyan-400">{deleteGameConfirm.packages.length}</strong></p>
            </div>
            <p className="text-[11px] text-red-300">Los pedidos existentes se conservan para auditoría, pero el juego dejará de aparecer en el catálogo.</p>
            <div className="flex gap-3">
              <button data-testid="app-modals-button-9" onClick={() => setDeleteGameConfirm(null)} className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs">Cancelar</button>
              <button onClick={handleConfirmDeleteGame} data-testid="confirm-delete-game-btn"
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-xl text-xs shadow-lg">
                Eliminar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ============ STATUS CHANGE REASON MODAL ============ */}
      {statusChangeModal && (
        <Modal onClose={() => setStatusChangeModal(null)} size="sm" testId="status-reason-modal">
          <div className="space-y-4">
            <div className="text-center">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto ${statusChangeModal.newStatus === "blocked" ? "bg-red-950/60 border-2 border-red-500" : "bg-yellow-950/60 border-2 border-yellow-500"}`}>
                {statusChangeModal.newStatus === "blocked" ? <Lock className="w-7 h-7 text-red-400" /> : <Clock className="w-7 h-7 text-yellow-400" />}
              </div>
              <h3 className="text-lg font-extrabold text-white mt-3">
                {statusChangeModal.newStatus === "blocked" ? "Bloquear Usuario" : "Suspender Usuario"}
              </h3>
              <p className="text-xs text-zinc-400 mt-1">{statusChangeModal.user.email}</p>
            </div>

            <div className="p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-[11px] text-zinc-300">
              {statusChangeModal.newStatus === "blocked"
                ? "El usuario NO podrá iniciar sesión. Verá el motivo que escribas al intentar acceder."
                : "El usuario podrá iniciar sesión pero NO podrá crear pedidos. Verá el motivo al intentar comprar."}
            </div>

            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1.5 uppercase">Motivo (visible para el usuario)</label>
              <textarea data-testid="status-reason-input" value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value.slice(0, 500))}
                rows={3} placeholder="Ej: Uso de referencias falsas / Comportamiento sospechoso / En revisión..."
                className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-ag7-purple resize-none" />
              <p className="text-[10px] text-zinc-500 mt-1 text-right">{statusChangeReason.length}/500</p>
            </div>

            <div className="flex gap-3">
              <button data-testid="app-modals-button-10" onClick={() => setStatusChangeModal(null)}
                className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-2.5 rounded-xl text-xs">Cancelar</button>
              <button onClick={() => handleChangeUserStatus(statusChangeModal.user.id, statusChangeModal.newStatus, statusChangeReason)}
                data-testid="confirm-status-change-btn"
                className={`flex-1 text-white font-bold py-2.5 rounded-xl text-xs shadow-lg ${statusChangeModal.newStatus === "blocked" ? "bg-red-600 hover:bg-red-500" : "bg-yellow-600 hover:bg-yellow-500"}`}>
                {statusChangeModal.newStatus === "blocked" ? "Bloquear" : "Suspender"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ============ ORDER CHAT DRAWER ============ */}
      {chatOrder && (
        <ChatDrawer
          order={chatOrder}
          currentUserRole={activeRole}
          onClose={() => setChatOrder(null)}
          onMessagesChange={() => loadOrders()}
          showToast={showToast}
        />
      )}

      {/* ============ WELCOME MODAL (first-login for clients) ============ */}
      {user && showWelcomeModal && (
        <WelcomeModal
          userName={user.name || user.username || (user.email ? user.email.split("@")[0] : "gamer")}
          onStartTour={() => {
            tour?.markOffered?.();
            tour?.start?.();
          }}
          onClose={async () => {
            setShowWelcomeModal(false);
            tour?.markOffered?.();
            try { window.localStorage.setItem(`ag7_welcome_seen_${user.id}`, "1"); }
            catch (e) { console.warn("welcome_seen local persist failed:", e); }
            try { await api.markWelcomeSeen(); }
            catch (e) { console.warn("markWelcomeSeen server call failed (local flag stands):", e); }
            // Reflect in local user object so the modal doesn't re-open on refresh via /auth/me
            setUser((prev) => prev ? { ...prev, has_seen_welcome: true } : prev);
          }}
        />
      )}

      {/* ============ LIVE PURCHASE FEED (social proof toasts) ============ */}
      {user && activeRole === "client" && siteStatus.open && (
        <SocialProofFeed />
      )}

      {/* ============ SUPPORT: FLOATING WHATSAPP BUTTON ============ */}
      {user && activeRole === "client" && !showSupportModal && (
        <button
          onClick={openSupportModal}
          data-testid="support-fab-btn"
          aria-label="Reportar un problema por WhatsApp"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 shadow-2xl shadow-green-500/40 border-2 border-green-300/30 flex items-center justify-center group animate-[pulse_3s_ease-in-out_infinite]"
        >
          <MessageCircle className="w-7 h-7 text-white group-hover:scale-110 transition-transform" fill="white" strokeWidth={0} />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-black animate-ping"></span>
        </button>
      )}

      {/* ============ SUPPORT: REPORT MODAL ============ */}
      {showSupportModal && (
        <Modal onClose={() => !supportSubmitting && setShowSupportModal(false)} size="sm" testId="support-modal">
          <div className={MODAL_HEADER}>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-green-950/60 border border-green-500 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-green-400" fill="currentColor" strokeWidth={0} />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Reportar un problema</h3>
                <p className="text-[10px] font-mono text-zinc-500">Envío directo por WhatsApp</p>
              </div>
            </div>
            <button data-testid="app-modals-button-11"
              onClick={() => !supportSubmitting && setShowSupportModal(false)}
              className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form data-testid="app-modals-form-12" onSubmit={handleSubmitSupport} className="space-y-4 pt-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block mb-1.5">
                Motivo / Descripción del problema
              </label>
              <textarea
                value={supportDesc}
                onChange={(e) => setSupportDesc(e.target.value)}
                data-testid="support-desc-input"
                rows={4}
                maxLength={500}
                placeholder="Ej. Pagué mi recarga hace 15 minutos y aún no llega."
                className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl p-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-green-500 resize-none"
                required
              />
              <p className="text-[10px] font-mono text-zinc-500 mt-1">{supportDesc.length}/500</p>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block mb-1.5">
                Adjuntar captura (opcional)
              </label>
              <label className="flex items-center gap-2 bg-zinc-950 hover:bg-ag7-surface-2 border border-[var(--ag7-border-soft)] hover:border-green-500 text-zinc-300 text-xs font-bold py-3 px-3 rounded-xl cursor-pointer transition-colors">
                <Paperclip className="w-4 h-4" />
                <span className="truncate flex-1">
                  {supportFile ? supportFile.name : "Seleccionar imagen (PNG, JPG, WEBP, GIF)"}
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => setSupportFile(e.target.files?.[0] || null)}
                  data-testid="support-file-input"
                  className="hidden"
                />
              </label>
              {supportFile && (
                <p className="text-[10px] font-mono text-green-300 mt-1">
                  {supportFile.name} · {(supportFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block mb-1.5">
                Pedido / UID afectado (opcional)
              </label>
              <input
                type="text"
                value={supportOrderId}
                onChange={(e) => setSupportOrderId(e.target.value)}
                data-testid="support-order-input"
                maxLength={80}
                placeholder="Ej. ORD-A1B2C3 o UID: 1234567890"
                className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-3 py-2.5 text-sm font-mono text-white placeholder-zinc-500 focus:outline-none focus:border-green-500"
              />
              {supportOrderId && (
                <p className="text-[10px] font-mono text-green-300 mt-1">Auto-completado desde tu pantalla</p>
              )}
            </div>

            <div className={MODAL_ACTIONS}>
              <button data-testid="app-modals-button-13"
                type="button"
                onClick={() => setShowSupportModal(false)}
                disabled={supportSubmitting}
                className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={supportSubmitting || supportDesc.trim().length < 5}
                data-testid="support-submit-btn"
                className="flex-1 bg-gradient-to-r from-green-600 to-green-500 hover:opacity-95 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2"
              >
                {supportSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Subiendo...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Enviar a WhatsApp</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ============ ADMIN: NEW ORDER TOAST (pulse banner) ============ */}
      {newOrderBanner && (
        <div
          data-testid="admin-new-order-banner"
          className="fixed top-16 sm:top-20 left-1/2 -translate-x-1/2 z-[55] px-5 py-3 rounded-2xl border-2 border-cyan-400 bg-gradient-to-r from-cyan-900/95 to-blue-900/95 shadow-2xl shadow-cyan-500/40 animate-pulse text-white text-sm font-bold flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-cyan-300" />
          <span>¡{newOrderBanner.count === 1 ? "Nuevo pedido" : `${newOrderBanner.count} pedidos nuevos`} recibido{newOrderBanner.count === 1 ? "" : "s"}!</span>
          <button
            onClick={() => setNewOrderBanner(null)}
            data-testid="dismiss-new-order-banner"
            className="ml-2 bg-black/30 hover:bg-black/50 p-1 rounded-full">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ============ ADMIN: NEW CHAT MESSAGE TOAST ============ */}
      {newChatBanner && (
        <div
          data-testid="admin-new-chat-banner"
          className="fixed top-28 sm:top-32 left-1/2 -translate-x-1/2 z-[55] px-5 py-3 rounded-2xl border-2 border-purple-400 bg-gradient-to-r from-purple-900/95 to-fuchsia-900/95 shadow-2xl shadow-purple-500/40 text-white text-sm font-bold flex items-center gap-2 animate-fade-in"
        >
          <MessageCircle className="w-5 h-5 text-purple-200" />
          <span>
            {newChatBanner.count === 1 ? "Nuevo mensaje del cliente" : `${newChatBanner.count} mensajes nuevos`}
          </span>
          <button
            onClick={() => setNewChatBanner(null)}
            data-testid="dismiss-new-chat-banner"
            className="ml-2 bg-black/30 hover:bg-black/50 p-1 rounded-full">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* CLIENT HISTORY MODAL — extracted to /components/modals/UserProfileModal.jsx */}
      <UserProfileModal />
      {/* ADJUSTMENT MODALS — new order-adjustment workflow */}
      <AdjustmentRequestModal />
      <AdjustmentPayModal />
      <ManualVerifyModal />


      {/* ============ ADMIN: REJECT ORDER MODAL ============ */}
      {rejectOrderModal && (
        <Modal onClose={() => !rejectingOrder && setRejectOrderModal(null)} size="sm" testId="reject-order-modal">
          <div className="space-y-4">
            <div className={MODAL_HEADER}>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-red-950/60 border border-red-500 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Rechazar Pedido</h3>
                  <p className="text-[10px] font-mono text-zinc-500">{rejectOrderModal.id} · {rejectOrderModal.gameName}</p>
                </div>
              </div>
              <button data-testid="app-modals-button-14" onClick={() => !rejectingOrder && setRejectOrderModal(null)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-xs space-y-1 font-mono">
              <p><span className="text-zinc-500">Paquete:</span> <strong className="text-white">{rejectOrderModal.packageName}</strong></p>
              <p><span className="text-zinc-500">Player ID:</span> <strong className="text-cyan-400">{rejectOrderModal.playerId}</strong></p>
              <p><span className="text-zinc-500">Ref pago:</span> <strong className="text-green-400">{rejectOrderModal.referenceNumber}</strong></p>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block mb-1.5">
                Motivo del rechazo (visible al cliente)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="reject-reason-input"
                rows={4}
                maxLength={500}
                placeholder="Ej. La cédula del comprobante no coincide con el titular registrado."
                className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl p-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500 resize-none"
              />
              <p className="text-[10px] font-mono text-zinc-500 mt-1">{rejectReason.length}/500</p>
            </div>

            {/* Surplus (excedente) reclaim toggle — visible only if this order
                actually credited a surplus to the user's wallet. Default is
                ON (reclaim), because rejecting the whole payment should not
                leave a phantom credit behind. Admin can leave it OFF for a
                goodwill exception. */}
            {Number(rejectOrderModal.surplusBs) > 0.01
              && rejectOrderModal.surplusCredited === true
              && !rejectOrderModal.surplusReclaimed && (
              <label
                data-testid="reject-reclaim-surplus-toggle"
                className="flex items-start gap-3 p-3 rounded-xl bg-amber-950/40 border border-amber-700/60 text-xs cursor-pointer">
                <input data-testid="app-modals-input-15"
                  type="checkbox"
                  checked={reclaimSurplusOnReject}
                  onChange={(e) => setReclaimSurplusOnReject(e.target.checked)}
                  className="mt-0.5 accent-amber-400"
                />
                <span className="text-amber-100">
                  Revocar el saldo a favor (<strong>Bs {formatBs(Number(rejectOrderModal.surplusBs)).replace('Bs ', '')}</strong>) que se acreditó al usuario al registrar este pago.
                  <br />
                  <span className="text-amber-300/70 text-[10px]">Recomendado — el pedido se está rechazando en su totalidad.</span>
                </span>
              </label>
            )}

            <div className={MODAL_ACTIONS}>
              <button data-testid="app-modals-button-16"
                type="button"
                onClick={() => setRejectOrderModal(null)}
                disabled={rejectingOrder}
                className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmRejectOrder}
                disabled={rejectingOrder || rejectReason.trim().length < 5}
                data-testid="confirm-reject-order-btn"
                className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2">
                {rejectingOrder ? <RefreshCw className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                <span>{rejectingOrder ? "Rechazando..." : "Rechazar Pedido"}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ============ PREMIUM COMPLETION CELEBRATION ============ */}
      {(() => {
        const celebrationOrders = (completedNotif?.orders || []).filter((order) => {
          const items = order.items?.length ? order.items : [order];
          return items.some(celebrationGameKey);
        });
        if (!celebrationOrders.length) return null;
        const ids = celebrationOrders.map((order) => order.id);
        const dismiss = () => acknowledgeCompletionOrders(ids);
        return <RechargeSuccessCelebration
          orders={celebrationOrders}
          games={games}
          socialNetworks={socialNetworks}
          onClose={dismiss}
          onViewOrders={() => { dismiss(); setClientTab("orders"); }}
          onBackToStore={() => { dismiss(); setClientTab("store"); }}
        />;
      })()}

      {/* ============ COMPLETED ORDER NOTIFICATION (otros juegos) ============ */}
      {(() => {
        const genericOrders = (completedNotif?.orders || []).filter((order) => {
          const items = order.items?.length ? order.items : [order];
          return !items.some(celebrationGameKey);
        });
        if (!genericOrders.length) return null;
        const dismiss = () => acknowledgeCompletionOrders(genericOrders.map((order) => order.id));
        return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in" data-testid="completed-notification">
          <div className="w-full max-w-md bg-gradient-to-br from-green-900/95 via-emerald-950/95 to-teal-900/95 border-4 border-green-400 rounded-3xl p-8 shadow-2xl shadow-green-500/40 space-y-5 text-center max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-green-500/20 border-4 border-green-400 flex items-center justify-center mx-auto animate-pulse">
                <CheckCircle2 className="w-12 h-12 text-green-300" />
              </div>
              <div className="absolute inset-0 rounded-full bg-green-400/30 blur-2xl -z-10"></div>
            </div>
            <div>
              <h2 className="text-3xl font-black text-white tracking-wider">
                {genericOrders.length > 1 ? "¡RECARGAS REALIZADAS!" : "¡RECARGA REALIZADA!"}
              </h2>
              <p className="text-xs text-green-200 mt-1 font-mono tracking-widest" data-testid="completion-count">
                ✓ {genericOrders.length > 1
                  ? `${genericOrders.length} PEDIDOS COMPLETADOS CON ÉXITO`
                  : "COMPLETADA CON ÉXITO"}
              </p>
            </div>
            <div className="space-y-2">
              {genericOrders.map(o => (
                <div key={o.id} data-testid={`completion-item-${o.id}`}
                  className="p-4 rounded-2xl bg-black/40 border border-green-500/40 space-y-1 text-sm">
                  <p className="font-bold text-white">{o.gameName}</p>
                  <p className="text-green-200">{o.packageName}</p>
                  <p className="text-[10px] font-mono text-green-300/70 mt-2">Pedido: {o.id}</p>
                </div>
              ))}
            </div>
            <div className="p-3 rounded-xl bg-green-950/60 border border-green-500/30 text-green-200 text-xs">
              🎮 Por favor verifica en tu cuenta, ya tienes el pedido acreditado.
            </div>

            {/* Follow-us CTA using admin-configured active social networks */}
            {socialNetworks.length > 0 && (
              <div
                data-testid="completion-social-cta"
                className="p-4 rounded-2xl bg-black/40 border border-green-500/30 space-y-2"
              >
                <p className="text-[11px] font-mono uppercase tracking-widest text-green-200 flex items-center justify-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Síguenos para mantenerte informado</span>
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {socialNetworks?.map(net => (
                    <a
                      key={net.id}
                      href={net.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`completion-social-link-${net.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-500/15 hover:bg-green-500/25 border border-green-400/40 text-green-100 text-xs font-bold transition-all"
                    >
                      {net.icon
                        ? <span className="text-base leading-none">{net.icon}</span>
                        : <ArrowUpRight className="w-3.5 h-3.5" />}
                      <span>{net.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={playChimeSound} data-testid="replay-chime-btn"
                className="px-4 py-3 bg-black/40 hover:bg-black/60 text-green-200 font-bold rounded-xl text-xs border border-green-500/30">
                🔔
              </button>
              <button onClick={dismiss} data-testid="dismiss-completion-btn"
                className="flex-1 bg-green-500 hover:bg-green-400 text-black font-black py-3 rounded-xl text-sm shadow-lg tracking-wider">
                ¡ENTENDIDO!
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ============ DELETE USER CONFIRMATION ============ */}
      {deleteUserConfirm && (
        <Modal onClose={() => setDeleteUserConfirm(null)} size="sm" testId="delete-user-modal">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-950/60 border-2 border-red-500 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-xl font-extrabold text-white">¿Eliminar Usuario?</h3>
            <div className="p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-xs text-zinc-300 space-y-1 font-mono text-left">
              <p><span className="text-zinc-500">Nombre:</span> <strong className="text-white">{deleteUserConfirm.name}</strong></p>
              <p><span className="text-zinc-500">Email:</span> <strong className="text-white">{deleteUserConfirm.email}</strong></p>
              <p><span className="text-zinc-500">Pedidos:</span> <strong className="text-cyan-400">{deleteUserConfirm.total_orders}</strong> (se preservan para auditoría)</p>
              <p><span className="text-zinc-500">Saldo a favor:</span> <strong className="text-purple-300">Bs {Math.round(deleteUserConfirm.credit_balance_bs)}</strong> (se pierde)</p>
            </div>
            <p className="text-[11px] text-red-300">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button data-testid="app-modals-button-17" onClick={() => setDeleteUserConfirm(null)} className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs">Cancelar</button>
              <button onClick={handleDeleteUser} data-testid="confirm-delete-user-btn"
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-xl text-xs shadow-lg">
                Eliminar Definitivamente
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ============ ADD / EDIT PAYMENT METHOD MODAL ============ */}
      {showAddBankModal && (
        <Modal onClose={() => { setShowAddBankModal(false); setEditingPm(null); }} size="sm" testId="add-bank-modal">
          <div className={MODAL_HEADER}>
            <h3 className="text-lg font-heading font-extrabold tracking-tight text-white">{editingPm ? "Editar Método de Pago" : "Agregar Cuenta / Método de Pago"}</h3>
            <button data-testid="app-modals-button-18" onClick={() => { setShowAddBankModal(false); setEditingPm(null); }} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full"><X className="w-5 h-5" /></button>
          </div>
          <form data-testid="app-modals-form-19" onSubmit={handleAddBank} className="space-y-3 pt-4">
            {/* Type selector */}
            <div>
              <label className="block text-[11px] font-mono uppercase text-zinc-400 mb-1.5">Tipo de Cuenta</label>
              <div className="grid grid-cols-3 gap-2" data-testid="pm-type-selector">
                {[
                  { id: "PAGO_MOVIL", label: "Pago Móvil", color: "cyan" },
                  { id: "TRANSFERENCIA", label: "Transferencia", color: "purple" },
                  { id: "USD_PLATFORM", label: "Pago en $", color: "green" },
                ].map(opt => {
                  const active = bankForm.type === opt.id;
                  const cls = active
                    ? { cyan: "bg-cyan-500/25 border-cyan-400 text-cyan-100", purple: "bg-purple-500/25 border-purple-400 text-purple-100", green: "bg-green-500/25 border-green-400 text-green-100" }[opt.color]
                    : "bg-ag7-bg border-[var(--ag7-border-soft)] text-zinc-400 hover:text-white";
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setBankForm({ ...bankForm, type: opt.id })}
                      data-testid={`pm-type-${opt.id}`}
                      className={`px-2 py-2.5 rounded-xl border text-[11px] font-bold uppercase tracking-widest ${cls}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dynamic fields */}
            {bankForm.type === "PAGO_MOVIL" && (
              <div className="space-y-3" data-testid="pm-fields-pago-movil">
                <FormField data-testid="bank-name-input" label="Banco" testId="bank-name-input" required value={bankForm.bankName} onChange={(v) => setBankForm({ ...bankForm, bankName: v })} placeholder="Ej. Banesco / BdV" />
                <FormField data-testid="bank-code-input" label="Código Banco" testId="bank-code-input" required value={bankForm.bankCode} onChange={(v) => setBankForm({ ...bankForm, bankCode: v })} placeholder="Ej. 0134" />
                <FormField data-testid="bank-holder-input" label="Titular" testId="bank-holder-input" required value={bankForm.accountHolder} onChange={(v) => setBankForm({ ...bankForm, accountHolder: v })} placeholder="AG7 STORE C.A." />
                <FormField data-testid="bank-doc-input" label="Cédula/RIF del Titular" testId="bank-doc-input" required value={bankForm.documentId} onChange={(v) => setBankForm({ ...bankForm, documentId: v })} placeholder="V-12345678" />
                <FormField data-testid="bank-phone-input" label="Teléfono (Pago Móvil)" testId="bank-phone-input" required value={bankForm.phone} onChange={(v) => setBankForm({ ...bankForm, phone: v })} placeholder="04121234567" />
              </div>
            )}

            {bankForm.type === "TRANSFERENCIA" && (
              <div className="space-y-3" data-testid="pm-fields-transferencia">
                <FormField data-testid="bank-name-input" label="Banco" testId="bank-name-input" required value={bankForm.bankName} onChange={(v) => setBankForm({ ...bankForm, bankName: v })} placeholder="Ej. Banesco" />
                <FormField data-testid="bank-holder-input" label="Titular" testId="bank-holder-input" required value={bankForm.accountHolder} onChange={(v) => setBankForm({ ...bankForm, accountHolder: v })} placeholder="AG7 STORE C.A." />
                <FormField data-testid="bank-doc-input" label="Cédula/RIF del Titular" testId="bank-doc-input" required value={bankForm.documentId} onChange={(v) => setBankForm({ ...bankForm, documentId: v })} placeholder="V-12345678" />
                <FormField data-testid="bank-account-input" label="Nro. Cuenta (20 dígitos)" testId="bank-account-input" required value={bankForm.accountNumber} onChange={(v) => setBankForm({ ...bankForm, accountNumber: v })} placeholder="01340000001234567890" />
              </div>
            )}

            {bankForm.type === "USD_PLATFORM" && (
              <div className="space-y-3" data-testid="pm-fields-usd">
                <FormField data-testid="pm-platform-name-input" label="Plataforma" testId="pm-platform-name-input" required value={bankForm.platformName} onChange={(v) => setBankForm({ ...bankForm, platformName: v })} placeholder="Zinli / Binance / Zelle / Payeer" />
                <FormField data-testid="pm-account-identifier-input" label="Correo o ID de la cuenta" testId="pm-account-identifier-input" required value={bankForm.accountIdentifier} onChange={(v) => setBankForm({ ...bankForm, accountIdentifier: v })} placeholder="ag7@correo.com o ID / dirección" />
                <FormField data-testid="pm-usd-holder-input" label="Titular (opcional)" testId="pm-usd-holder-input" value={bankForm.accountHolder} onChange={(v) => setBankForm({ ...bankForm, accountHolder: v })} placeholder="Nombre del titular" />
              </div>
            )}

            <label className="flex items-center gap-2 p-2.5 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] cursor-pointer">
              <input
                type="checkbox"
                data-testid="pm-active-toggle"
                checked={!!bankForm.active}
                onChange={(e) => setBankForm({ ...bankForm, active: e.target.checked })}
                className="w-4 h-4 accent-green-500"
              />
              <span className="text-xs text-zinc-200">Método activo (visible para los clientes)</span>
            </label>

            <div className={MODAL_ACTIONS}>
              <button data-testid="app-modals-button-20" type="button" onClick={() => { setShowAddBankModal(false); setEditingPm(null); }} className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-2.5 rounded-xl text-xs">Cancelar</button>
              <button type="submit" data-testid="save-bank-submit-btn" className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2.5 rounded-xl text-xs">{editingPm ? "Guardar Cambios" : "Guardar Cuenta"}</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
